import { type JSXElementConstructor, memo, type MouseEvent } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { cn } from "../../tailwind-utils";
import { Tooltip } from "../../tooltip/tooltip";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import { useToolbarOverflow } from "./toolbar-overflow";

export function Toolbar({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("relative flex select-none items-center", className)}
      role="toolbar"
      {...props}
    />
  );
}

export const ToolbarButton = memo(function ToolbarButton({
  Icon,
  tooltip,
  pressed,
  onClick,
  disabled,
}: {
  Icon: JSXElementConstructor<object>;
  tooltip?: string;
  pressed?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}) {
  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    const label = tooltip?.replace(/\s*\(.*\)$/, "") ?? "";
    return (
      <DropdownMenuItem
        disabled={disabled}
        onSelect={(e) =>
          onClick?.(e as unknown as MouseEvent<HTMLButtonElement>)
        }
      >
        <Icon />
        {label}
      </DropdownMenuItem>
    );
  }

  const button = (
    <RedoButton
      disabled={disabled}
      hierarchy="tertiary"
      IconLeading={Icon}
      onClick={onClick}
      pressed={pressed}
      size="md"
    />
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{button}</Tooltip>;
  }

  return button;
});

export function ToolbarGroup({
  children,
  className,
}: React.ComponentProps<"div">) {
  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    return <>{children}</>;
  }

  return (
    <div
      className={cn(
        "group/toolbar-group",
        "relative hidden has-[button]:flex items-center",
        className,
      )}
    >
      <div className="flex items-center">{children}</div>
      <div className="group-last/toolbar-group:hidden! mx-1.5 h-5 w-px bg-border" />
    </div>
  );
}
